import React, { useImperativeHandle, useEffect, useState, useMemo } from 'react';
import { Alert, Select, Spin } from 'antd';
import useBrands from '../../gql/hooks/useBrands'; 
import useCertificates from '../../gql/hooks/useCertificates'; 
import useHasError from '../../hooks/useHasError'; 
import useIsLoading from '../../hooks/useIsLoading'; 
import StrongTag from '../StrongTag/StrongTag';
import './styles.less';

const FormCertificates = ( { form, modifiedCallback, ref } ) => {    
    const { brands, brandsLoading, brandsError,  } = useBrands();
    const { certificates, certificatesLoading, certificatesError } = useCertificates();
    const { hasError } = useHasError( { items: [ brandsError, certificatesError ] } );
    const { isLoading } = useIsLoading( { items: [ brandsLoading, certificatesLoading ] } );
    const [ selectedCertificates, setSelectedCertificates ] = useState( [] );

    useImperativeHandle( ref, () => ( {
        reset() {
            form.setFieldsValue( { certificateIdsAreModified: false } ); 
            form.setFieldsValue( { certificateIdsModified: [] } ); 
            setSelectedCertificates( form.getFieldValue( 'certificateIds' ) );
            modifiedCallback( false ); 
        }, 
        getBrandId( certId ) {
            return getBrandIdForCertificate( certId ).brandId;
        }
    } ) );
    
    useEffect( () => certificates && setSelectedCertificates( form.getFieldValue( 'certificateIds' ) ), 
        [ certificates, setSelectedCertificates ] );

    const certificateGroups = useMemo( () => {
        if ( brands && certificates ) {
            const result = [  ...brands ];
            result.forEach( i => i.certificates = certificates.filter( c => i.id === c.brandId ) );
            return result;
        } else {
            return [];
        }
    }, [ brands, certificates ] );

    const getBrandIdForCertificate = ( id ) => {
        return certificates.find( i => i.id === id );
    };

    const updateModified = () => { 
        const oldCerts = form.getFieldValue( 'certificateIds' ).sort( ( a, b ) => a.localeCompare( ( b ) ) );
        const newCerts = form.getFieldValue( 'certificateIdsModified' ).sort( ( a, b ) => a.localeCompare( ( b ) ) );
         
        if ( JSON.stringify( newCerts ) !== JSON.stringify( oldCerts ) ) {
            form.setFieldsValue( { certificateIdsAreModified: true } );  
            modifiedCallback( true );
        } else {
            form.setFieldsValue( { certificateIdsAreModified: false } );  
            modifiedCallback( false );
        }
    };

    const handleCertificateChange = ( values ) => {

        // Early return for no values
        if ( values.length === 0 ) {
            setSelectedCertificates( [] );
            form.setFieldsValue( { certificateIdsModified: [] } ); 
            updateModified();
            return;
        }

        const newItem = values[ values.length - 1 ];         
        const newlySelectedWithBrandId = values.map( i => ( { id: i, brand: getBrandIdForCertificate( i ) } ) );
        const result = [];

        brands.forEach( b => {
            // Get newly selected for this brand
            const items = newlySelectedWithBrandId.filter( i => b.id === i.brand.brandId );

            // If one, push it
            if ( items.length === 1 ) {
                result.push( items[ 0 ].id );
            } else {
                // If more than one replace old one
                if ( items.length > 1 ) {
                    const newCert = newlySelectedWithBrandId.find( i => i.id === newItem );
                    
                    if ( newCert ) {
                        result.push( newCert.id );
                    }
                }
            } 
        } );

        form.setFieldsValue( { certificateIdsModified: result } ); 
        setSelectedCertificates( result ); 
        updateModified();
    };

    return (
        <div className="form-item-certificates">
            
            { isLoading && 
                <Spin />
            }
            
            { hasError && 
                <Alert 
                    type="error"
                    message="Error"
                    description="An unknown error occurred. Please try again later."
                />
            }

            { !isLoading && !hasError &&
                <>
                    <h4>Certificates</h4>
                    <Select
                        mode="multiple" 
                        popupClassName="certificates-select"
                        value={ selectedCertificates }
                        placeholder="Select certificates" 
                        onChange={ handleCertificateChange }
                        filterOption={ ( input, option ) =>
                            ( option?.children ?  option.children[ 0 ] : '' ).toLowerCase().includes( input.toLowerCase() )
                        }
                    >
                        {
                            certificateGroups.map( g => {
                                return (
                                    <Select.OptGroup key={ g.id } label={ g.name }>
                                        {
                                            g.certificates.map( c => {
                                                return (
                                                    <Select.Option 
                                                        key={ c.id } 
                                                        value={ c.id }
                                                    >
                                                        { c.name }
                                                        <StrongTag 
                                                            className="pad-left"
                                                            color="blue"
                                                            strontText={ g.name }
                                                        />
                                                    </Select.Option>
                                                );
                                            } )
                                        }
                                    </Select.OptGroup>
                                );
                            } )
                        }
                    </Select>
                </>
            }
        </div>
    );
};

export default FormCertificates;
